import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { EditableProduct } from '../../../types';
import cloneDeep from 'lodash/cloneDeep';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map, tap } from 'rxjs/operators';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';

@Component({
    selector: 'lib-composed-images-drawer',
    templateUrl: './composed-images-drawer.component.html',
    styleUrls: ['./composed-images-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ComposedImagesDrawerComponent
  extends AliveState
  implements OnInit {
  @Input() images: Array<EditableProduct.ComposedImage> = [];
  currentImage$ = new BehaviorSubject('');
  @Output() configPicked = new EventEmitter();
  isPhonePortrait$ = this.screenObserver
    .observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape])
    .pipe(map(({ matches }) => matches));
  currentIndex = 0;

  @Input() set selectPhoto(config: { [x: string]: any }) {
    config = cloneDeep(config);
    const originalConfiguration: Array<{
      value: string | boolean | number;
      id: string;
    }> = [];
    const copyConfiguration: Array<{
      config: Array<{
        value: string | boolean | number;
        id: string;
      }>;
    }> = [];
    Object.entries(config).forEach((value) => {
      originalConfiguration.push({ value: value[1], id: value[0] });
    });
    const photoConfig: Array<{
      fileName: string;
      config: Array<{
        value: string | boolean | number;
        id: string;
      }>;
    }> = [];
    Object.entries(this.images).forEach((image) => {
      if (this.images[image[0]].config) {
        photoConfig.push({
          fileName: this.images[image[0]].fileName,
          config: [],
        });
        const copyConfig = [];
        Object.entries(this.images[image[0]].config).forEach(
          (value: {} | string) => {
            if (typeof value[1]?.value === 'boolean') {
              photoConfig
                .find(
                  (value1) => value1.fileName === this.images[image[0]].fileName
                )
                .config.push({ value: value[1].value, id: value[0] });
              copyConfig.push(
                originalConfiguration.find((value1) => value1.id === value[0])
              );
            }
            if (typeof value[1]?.min === 'number') {
              photoConfig
                .find(
                  (value1) => value1.fileName === this.images[image[0]].fileName
                )
                .config.push({ value: value[1], id: value[0] });
              if (
                value[1].min <=
                  originalConfiguration.find((value1) => value1.id === value[0])
                    ?.value &&
                originalConfiguration.find((value1) => value1.id === value[0])
                  ?.value <= value[1].max
              ) {
                copyConfig.push({
                  value: value[1],
                  id: originalConfiguration.find(
                    (value1) => value1.id === value[0]
                  ).id,
                });
              }
            }
            if (typeof value[1]?.value === 'object') {
              photoConfig
                .find(
                  (value1) => value1.fileName === this.images[image[0]].fileName
                )
                .config.push({ value: value[1].value[0], id: value[0] });
              copyConfig.push(
                originalConfiguration.find((value1) => value1.id === value[0])
              );
            }
          }
        );
        copyConfiguration.push({ config: [...copyConfig] });
      }
    });
    copyConfiguration.forEach((copyConfig) => {
      if (
        photoConfig.find(
          (value2) =>
            JSON.stringify(value2.config) === JSON.stringify(copyConfig.config)
        )
      ) {
        this.currentImage$.next(
          photoConfig.find(
            (value2) =>
              JSON.stringify(value2.config) ===
              JSON.stringify(copyConfig.config)
          ).fileName
        );
      }
    });
  }

  constructor(private screenObserver: BreakpointObserver) {
    super();
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.currentImage$.pipe(
        tap((fileName) => {
          this.currentIndex = this.images.findIndex(
            (file) => file.fileName === fileName
          );
        })
      )
    );

    this.currentImage$.next(this.images[0].fileName);
  }

  prev() {
    if (this.currentIndex === 0) {
      this.currentImage$.next(this.images[this.images.length - 1].fileName);
    } else {
      this.currentImage$.next(this.images[this.currentIndex - 1].fileName);
    }
  }

  next() {
    if (this.currentIndex === this.images.length - 1) {
      this.currentImage$.next(this.images[0].fileName);
    } else {
      this.currentImage$.next(this.images[this.currentIndex + 1].fileName);
    }
  }

  imageClicked(image: EditableProduct.ComposedImage) {
    this.currentImage$.next(image.fileName);
    if (image.hasPreConfig) {
      this.configPicked.emit(image.config);
    }
  }
}
